/**
 * Hosts {@link NsfPanel} on the banking page.
 *
 * Connection-scoped like {@link ChargebackSection}, and for a sharper
 * reason: an NSF item is a deadline. The bank lists it once and decides
 * for us when the expiry passes, so the queue has to be somewhere a
 * manager sees every morning, not on one association's page among many.
 *
 * Read side only. The pay/return controls arrive after the 2026-09-29
 * call with the bank settles the open questions about the image id, the
 * request status values and what happens at expiry.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Subheading } from './common/Catalyst/heading';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { ConnectionPicker } from './bank/linear/ConnectionPicker';
import { bankService } from './clients/ApiClients';
import { BankConnectionReturn } from './clients/rust-api-client';
import { getCompanyIdFromToken } from './utils/TokenUtils';
import { getBankErrorParts } from './utils/bankHelpers';
import NsfPanel from './NsfPanel';

const NsfSection: React.FC = () => {
  const companyId = useMemo(() => getCompanyIdFromToken(), []);
  const [connections, setConnections] = useState<BankConnectionReturn[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      const response = await bankService.listCompanyConnections({
        companyId,
      });
      const active = response.data.filter((c) => c.is_active);
      setConnections(active);
      // Open on the connection that has something to decide. The
      // dashboard counts an association's items across connections and
      // links here; landing on a connection with an empty queue reads as
      // "nothing to do" when the deadline is on the next one over.
      let first = active[0]?.id ?? null;
      if (active.length > 1) {
        const counts = await Promise.all(
          active.map((c) =>
            bankService
              .listConnectionNsf({ connectionId: c.id, openOnly: true })
              .then((r) => r.data.open)
              .catch(() => 0)
          )
        );
        const busiest = counts.indexOf(Math.max(...counts));
        if (counts[busiest] > 0) first = active[busiest].id;
      }
      setSelected((current) => current ?? first);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setError(body || "Couldn't load bank connections.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="mt-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Subheading>Insufficient funds</Subheading>
          <Text className="mt-1 text-sm">
            Checks and debits presented against an association’s account without
            the funds to cover them. Each one has a deadline; the bank decides
            for you once it passes.
          </Text>
        </div>
        {connections.length > 1 && (
          <div>
            <ConnectionPicker
              className="border border-line"
              connections={connections}
              value={selected}
              onChange={setSelected}
            />
          </div>
        )}
      </div>

      <ErrorAlert className="mt-4">{error}</ErrorAlert>
      {!loading && !error && connections.length === 0 && (
        <Text className="mt-4">
          No active bank connection. Add one above to see NSF items.
        </Text>
      )}
      {selected && <NsfPanel connectionId={selected} />}
    </section>
  );
};

export default NsfSection;
